/**
 * @fileoverview The landing page's words, in one place (OQ-48).
 *
 * Kept apart from the components so that an English version is a second
 * object of the same shape rather than a hunt through JSX (CLAUDE.md 「書き方」).
 * The claims are the ones docs/01-service-overview.md makes, limited to what
 * the product does today: no pricing, nothing about terms we have not written
 * yet (N-L-03). Since 2026-09-28 a signed-in person can create an organization
 * (OQ-51), so the page no longer says new organizations are not taken.
 * Since 2026-09-30 it also says what came after the page was first written:
 * the shared description, ratings and visits (OQ-52), sorting and searching
 * the description (OQ-57), the shops' pages (OQ-53), joining by email domain
 * (OQ-56) and the home screen (OQ-58). Members' notes are gone (OQ-52), and so
 * are they from here.
 */
export const LANDING_COPY = {
  brand: 'MapShare',
  signIn: 'サインイン',
  hero: {
    title: ['チームのお店リストを、', '組織の地図に。'],
    lead: [
      'チームのみんなが見つけたお店が、1つの地図に集まります。',
      '「今日のランチ」「来客の手土産」—— 用途で絞って、みんなの評価と行った記録を見れば、すぐに決められます。',
    ],
    notice: 'サインインすると、自分たちの組織を作って使いはじめられます。',
  },
  demo: {
    caption: '架空の組織「さくら商事」の地図です。触ってみてください。',
    searchLabel: 'お店を探す',
    searchPlaceholder: '店名・住所・説明で探す（例：個室）',
    tagsLabel: '用途で絞る',
    listHeading: (count: number) => `登録されたお店 ${count}件`,
    sortLabel: '並べ替え',
    excerpt: '説明: ',
    empty: '条件に合うお店はありません。',
    fictional: '架空',
    distance: (meters: string, minutes: number) => `直線約${meters}・徒歩${minutes}分`,
    addedBy: (name: string) => `登録: ${name}さん`,
    noRating: '評価なし',
    averageLabel: (average: number) => `平均 ${average}`,
    raters: (count: number) => `（${count}人）`,
    visitCount: (count: number) => `行った記録 ${count}件`,
    recentVisitors: (names: readonly string[]) => `最近行った人: ${names.join('、')}`,
    descriptionHeading: '説明',
    visitsHeading: '行った記録',
    noVisits: 'まだ行った記録はありません。',
    moreVisits: (count: number) => `ほか ${count}件`,
    back: '一覧に戻る',
    mapLoading: '地図を読み込んでいます…',
    mapFailed: '地図を読み込めませんでした。一覧とカードは触れます。',
  },
  values: {
    heading: 'MapShare でできること',
    items: [
      {
        title: 'みんなで育てる地図',
        body: '誰かが登録したお店は、メンバー全員の地図に並びます。使うほど、チームの行きつけが増えていきます。',
      },
      {
        title: '用途で探せる',
        body: 'ランチも飲み会も手土産も混ざらない。組織で決めた用途タグで、見たいお店だけが地図に出ます。お昼どきに開くと「ランチ」から。',
      },
      {
        title: '評価と行った記録で選べる',
        body: '★の平均と、誰がいつ行って、どうだったか。評価の高い順・行った記録の多い順に並べ替えれば、迷わず決められます。',
      },
      {
        title: 'お店の情報を1か所に',
        body: '「個室は8名まで」「前日までに予約」。お店ごとの説明をメンバー全員で書き足せて、検索にもかかります。食べログや公式サイトへのリンクも一緒に。',
      },
      {
        title: 'メンバーを迎えやすい',
        body: 'メールアドレスで招待できます。会社のメールのドメインを登録しておけば、同じドメインの人は組織の URL を開くだけで参加。見るだけのゲストも選べます。',
      },
      {
        title: 'スマホのホーム画面から',
        body: 'ホーム画面に追加すれば、アプリのようにすぐ地図が開きます。アプリストアからのインストールは要りません。',
      },
    ],
  },
  steps: {
    heading: '登録は3ステップ',
    items: [
      { title: '店名で探す', body: '地図の上の検索欄に店名を入れて、候補から選びます。' },
      { title: 'ピンを合わせる', body: '地図の十字にお店の入口を合わせて確定します。' },
      {
        title: '用途と説明を添える',
        body: '「ランチ」などの用途と、「個室あり」などの説明を入れて保存します（どちらも任意）。',
      },
    ],
  },
  status: {
    heading: 'いまの状況',
    body: [
      'MapShare は、現在ベータ版としてどなたでもご利用いただけます。利用規約は準備中です。',
      'すでに組織のメンバーの方や、招待を受け取った方は、サインインするとご自分の組織の地図が開きます。',
      'はじめての方は、サインインして「組織を作る」から、自分たちの組織の地図を作れます。',
    ],
  },
  footer: {
    fictional: 'デモのお店・組織・メンバーと、その説明・評価・行った記録はすべて架空のものです。',
  },
  account: {
    dialogLabel: 'サインイン',
    close: '閉じる',
    loading: '読み込んでいます…',
    notConfigured: 'この環境ではサインインが設定されていません。',
    looking: 'あなたの組織を確かめています…',
    opening: (name: string) => `${name} の地図を開いています…`,
    chooseHeading: 'どの組織の地図を開きますか？',
    invited: '招待されています',
    none: [
      'このアカウントで開ける組織はありません。',
      '招待を受け取った方は、招待されたメールアドレスでサインインしているか確かめてください。',
    ],
    createAnother: '新しい組織を作る',
    create: {
      heading: '組織を作る',
      lead: 'あなたがオーナーになります。メンバーは、あとから招待できます。',
      nameLabel: '組織の名前',
      namePlaceholder: '例：さくら商事',
      nameRequired: '組織の名前を入力してください',
      slugLabel: '組織の URL',
      slugHint:
        '英小文字・数字・ハイフンで 3〜30 文字。メンバーがブックマークする URL になるので、あとから変えられないつもりで決めてください。',
      slugProblems: {
        invalid_format:
          'URL は英小文字・数字・ハイフンの 3〜30 文字で、ハイフンで始めたり終えたりできません',
        reserved_word: 'この URL は MapShare で使うため、組織には使えません',
        reserved_prefix: 'staging- や test- などで始まる URL は、組織には使えません',
      },
      submit: '作成する',
      sending: '作成しています…',
      cancel: 'やめる',
      created: (name: string) => `${name} の地図を開いています…`,
      failed: '組織を作れませんでした。時間をおいて、もう一度お試しください。',
    },
    otherAccount: '別のアカウントでサインインする',
    failed: '組織の一覧を読み込めませんでした。時間をおいて、もう一度お試しください。',
  },
} as const;
